import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Typography } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { FIELDS_LABEL, MODAL_BUTTON_KEYS } from "constants/addOption";
import {
  ADD_TENOR_TABLE_COLUMNS,
  ADD_TENOR_MODAL_WIDTH,
  EXISTING_TENOR_UPDATE_MESSAGE,
  createEmptyAddTenorRow,
  pcsLockedRowOverrides,
} from "constants/addTenorTable";
import { GlobalContext } from "contexts/GlobalContext";
import {
  validateAddTenorRowsByKey,
  validateAllAddTenorRows,
} from "helpers/Pricer/addTenorTableValidation";
import {
  buildBatchCreateTenorRequestFromSubmitGroups,
  buildCreateTenorRequestFromRowGroup,
  submitGroupsShareProductContractStyle,
} from "helpers/Pricer/buildCreateTenorRequest";
import {
  batchCreateOptionRowTenors,
  createOptionRowTenor,
} from "api/pricerApi";
import {
  getCreateTenorToastMessage,
  handleCreateTenorError,
  buildTenorSelectionPatch,
} from "helpers/Pricer/optionRowTenorCreation";
import {
  canRemoveAddTenorBlock,
  groupAddTenorRowsForSubmit,
  isAddTenorContinuationRow,
  isAddTenorExistingCatalogTenorRow,
  reexpandTenorGroupRows,
  removeTenorGroupRows,
  replaceRowWithExpandedSublegRows,
} from "helpers/Pricer/addTenorRowExpansion";
import { toast } from "helpers/generalHelper";
import { toastValidationErrors } from "helpers/toastValidationErrors";
import {
  useAddTenorCatalog,
  useResolveAddTenorCatalogFromPcs,
} from "hooks/useAddTenorCatalog";
import useDraggableModal from "hooks/useDraggableModal";
import {
  CancelButton,
  PrimaryButton,
  SecondaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import { PlusOutlined } from "@ant-design/icons";
import {
  RowProductSelect,
  RowContractSelect,
  RowStyleSelect,
  RowPeriodSelect,
  ExpiryCalendarLinkCell,
  TenorSelectCell,
  SubLegsCell,
  StartDateCell,
  EndDateCell,
  UlTenorSelectCell,
  ReadOnlyFieldCell,
} from "./AddTenorTableCells";
import AddTenorExcelGrid from "./AddTenorExcelGrid";
import "./AddTenorTablePopup.scss";

const { Text } = Typography;

function AddTenorTablePopup({
  open,
  onClose,
  defaultProductContractStyleId = null,
  defaultShortcutLabel = "",
}) {
  const queryClient = useQueryClient();
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const lockPcs = defaultProductContractStyleId != null;
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [submitProgress, setSubmitProgress] = useState("");

  const { modalRender, title: draggableTitle } = useDraggableModal(
    open,
    MODAL_BUTTON_KEYS.ADD_TENOR
  );
  const { productOptions, productsLoading } = useAddTenorCatalog();
  const { data: resolvedCatalog } = useResolveAddTenorCatalogFromPcs(
    defaultProductContractStyleId,
    open && lockPcs
  );

  const rowErrorsByKey = useMemo(
    () =>
      validationAttempted
        ? validateAddTenorRowsByKey(rows, defaultShortcutLabel)
        : {},
    [rows, validationAttempted, defaultShortcutLabel]
  );

  const errorRowCount = Object.keys(rowErrorsByKey).length;

  const emptyRowOverrides = useMemo(
    () =>
      lockPcs
        ? pcsLockedRowOverrides(
            defaultProductContractStyleId,
            defaultShortcutLabel
          )
        : {},
    [lockPcs, defaultProductContractStyleId, defaultShortcutLabel]
  );

  const updateRow = (key, patch) => {
    setRows((prev) => {
      const row = prev.find((item) => item.key === key);
      if (!row) return prev;

      const clearsTenorGroup =
        row.tenor_group_id != null &&
        (patch.tenor_label === "" ||
          (patch.period_type_id != null &&
            patch.period_type_id !== row.period_type_id) ||
          patch.tenor_group_id === null);

      if (clearsTenorGroup) {
        const remaining = prev.filter(
          (item) =>
            item.tenor_group_id !== row.tenor_group_id || item.key === key
        );
        return remaining.map((item) =>
          item.key === key ? { ...item, ...patch } : item
        );
      }

      if (patch.ul_tenor != null && row.tenor_group_id) {
        return prev.map((item) =>
          item.tenor_group_id === row.tenor_group_id
            ? { ...item, ul_tenor: patch.ul_tenor }
            : item.key === key
              ? { ...item, ...patch }
              : item
        );
      }

      return prev.map((item) =>
        item.key === key ? { ...item, ...patch } : item
      );
    });
  };

  const handleTenorSelect = (key, option, defaultSublegs) => {
    setRows((prev) => {
      const row = prev.find((item) => item.key === key);
      if (!row) return prev;
      const patch = buildTenorSelectionPatch(
        row,
        option,
        defaultShortcutLabel,
        defaultSublegs
      );
      return replaceRowWithExpandedSublegRows(
        prev,
        key,
        { ...row, ...patch },
        option,
        defaultShortcutLabel
      );
    });
  };

  const handleMonthsChange = (key, months) => {
    setRows((prev) =>
      reexpandTenorGroupRows(prev, key, months, defaultShortcutLabel)
    );
  };

  const handleRemoveRow = (key) => {
    setRows((prev) => {
      if (!canRemoveAddTenorBlock(prev, key)) return prev;
      const next = removeTenorGroupRows(prev, key);
      return next.length ? next : prev;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => {
      let next = createEmptyAddTenorRow(emptyRowOverrides);
      if (lockPcs && resolvedCatalog) {
        next = {
          ...next,
          product_id: resolvedCatalog.product_id,
          contract_id: resolvedCatalog.contract_id,
          style_id: resolvedCatalog.style_id,
          product_label: resolvedCatalog.product_label || next.product_label,
          contract_label: resolvedCatalog.contract_label || next.contract_label,
          style_display: resolvedCatalog.style_display || next.style_display,
        };
      } else {
        const template = prev.find(
          (row) =>
            row.product_id != null &&
            row.contract_id != null &&
            row.style_id != null
        );
        if (template) {
          next = {
            ...next,
            product_id: template.product_id,
            contract_id: template.contract_id,
            style_id: template.style_id,
            product_label: template.product_label,
            contract_label: template.contract_label,
            style_display: template.style_display,
            style_label: template.style_label,
          };
        }
      }
      return [...prev, next];
    });
  };

  useEffect(() => {
    if (!open) return;
    setRows([createEmptyAddTenorRow(emptyRowOverrides)]);
    setValidationAttempted(false);
    setSubmitProgress("");
  }, [open, emptyRowOverrides]);

  useEffect(() => {
    if (!open || !lockPcs || !resolvedCatalog) return;
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        product_id: resolvedCatalog.product_id,
        contract_id: resolvedCatalog.contract_id,
        style_id: resolvedCatalog.style_id,
        product_label: resolvedCatalog.product_label || row.product_label,
        contract_label: resolvedCatalog.contract_label || row.contract_label,
        style_display: resolvedCatalog.style_display || row.style_display,
      }))
    );
  }, [open, lockPcs, resolvedCatalog]);

  const invalidateTenorQueries = () => {
    queryClient.invalidateQueries({ queryKey: [FIELDS_LABEL.TENOR.key] });
    queryClient.invalidateQueries({ queryKey: [FIELDS_LABEL.BESPOKE_TENOR.key] });
    queryClient.invalidateQueries({ queryKey: ["add-tenor-missing"] });
    queryClient.invalidateQueries({ queryKey: ["add-tenor-form-context"] });
  };

  const handleSubmit = async () => {
    setValidationAttempted(true);
    const errors = validateAllAddTenorRows(rows, defaultShortcutLabel);
    if (errors.length) {
      toastValidationErrors(errors.join("\n"), {
        title: "Add Tenor validation",
        variant: "warning",
        duration: 6,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let lastResponse = null;
      const submitGroups = groupAddTenorRowsForSubmit(rows);
      const anyUpdate = submitGroups.some((groupRows) =>
        groupRows.some((item) => item.tenor_exists)
      );
      const useBatch =
        submitGroups.length > 1 &&
        submitGroupsShareProductContractStyle(submitGroups);

      if (useBatch) {
        setSubmitProgress(
          anyUpdate ? "Updating tenors…" : "Creating tenors…"
        );
        lastResponse = await batchCreateOptionRowTenors(
          buildBatchCreateTenorRequestFromSubmitGroups(
            submitGroups,
            defaultShortcutLabel
          )
        );
      } else {
        for (let i = 0; i < submitGroups.length; i++) {
          const groupRows = submitGroups[i];
          const isUpdate = groupRows.some((item) => item.tenor_exists);
          setSubmitProgress(isUpdate ? "Updating tenor…" : "Creating tenor…");
          lastResponse = await createOptionRowTenor(
            buildCreateTenorRequestFromRowGroup(
              groupRows,
              defaultShortcutLabel
            )
          );
        }
      }
      const msg = getCreateTenorToastMessage(lastResponse);
      toast(msg, "success");
      invalidateTenorQueries();
      onClose();
    } catch (error) {
      handleCreateTenorError(error);
    } finally {
      setIsSubmitting(false);
      setSubmitProgress("");
    }
  };

  const renderCell = (colKey, row, rowErrors) => {
    const cellProps = { row, updateRow, rowErrors, dark_Theme };
    const isContinuation = isAddTenorContinuationRow(row);

    switch (colKey) {
      case ADD_TENOR_TABLE_COLUMNS.PRODUCT:
        if (isContinuation || lockPcs) {
          return (
            <ReadOnlyFieldCell
              value={row.product_label}
              rowErrors={rowErrors}
            />
          );
        }
        return (
          <RowProductSelect
            {...cellProps}
            productOptions={productOptions}
            loading={productsLoading}
            disabled={lockPcs}
          />
        );
      case ADD_TENOR_TABLE_COLUMNS.CONTRACT:
        if (isContinuation || lockPcs) {
          return (
            <ReadOnlyFieldCell
              value={row.contract_label}
              rowErrors={rowErrors}
            />
          );
        }
        return <RowContractSelect {...cellProps} disabled={lockPcs} />;
      case ADD_TENOR_TABLE_COLUMNS.STYLE:
        if (isContinuation || lockPcs) {
          return (
            <ReadOnlyFieldCell
              value={row.style_display || row.style_label}
              rowErrors={rowErrors}
            />
          );
        }
        return (
          <RowStyleSelect
            {...cellProps}
            disabled={lockPcs}
            shortcutLabel={defaultShortcutLabel}
          />
        );
      case ADD_TENOR_TABLE_COLUMNS.PERIOD:
        if (isContinuation) {
          return (
            <ReadOnlyFieldCell value={row.period_label} rowErrors={rowErrors} />
          );
        }
        return <RowPeriodSelect {...cellProps} />;
      case ADD_TENOR_TABLE_COLUMNS.LINK:
        return (
          <ExpiryCalendarLinkCell
            row={row}
            isContinuation={isContinuation}
          />
        );
      case ADD_TENOR_TABLE_COLUMNS.TENOR:
        if (isContinuation) {
          return (
            <ReadOnlyFieldCell value={row.tenor_label} rowErrors={rowErrors} />
          );
        }
        return (
          <TenorSelectCell
            {...cellProps}
            shortcutLabel={defaultShortcutLabel}
            onTenorSelect={handleTenorSelect}
            onMonthsChange={handleMonthsChange}
          />
        );
      case ADD_TENOR_TABLE_COLUMNS.SUB_LEG:
        return <SubLegsCell row={row} rowErrors={rowErrors} />;
      case ADD_TENOR_TABLE_COLUMNS.START_DATE:
        return (
          <StartDateCell {...cellProps} shortcutLabel={defaultShortcutLabel} />
        );
      case ADD_TENOR_TABLE_COLUMNS.END_DATE:
        return <EndDateCell {...cellProps} />;
      case ADD_TENOR_TABLE_COLUMNS.UL_TENOR:
        if (isContinuation) {
          return (
            <ReadOnlyFieldCell value={row.ul_tenor} rowErrors={rowErrors} />
          );
        }
        return <UlTenorSelectCell {...cellProps} />;
      default:
        return null;
    }
  };

  const modalWidth = ADD_TENOR_MODAL_WIDTH;
  const isUpdatingExistingTenor = rows.some((row) =>
    isAddTenorExistingCatalogTenorRow(row, rows)
  );

  return (
    <Modal
      className={`add-tenor-table-popup ${dark_Theme ? "ant-modal-dark" : ""}`}
      title={draggableTitle}
      modalRender={modalRender}
      open={open}
      onCancel={onClose}
      destroyOnClose
      getContainer="#content-root"
      width={modalWidth}
      styles={{ body: { paddingTop: 12 } }}
      footer={
        <div className="add-tenor-table-popup__footer">
          <Text type="secondary" className="add-tenor-table-popup__footer-status">
            {validationAttempted && errorRowCount > 0
              ? `${errorRowCount} row(s) with issues`
              : ""}
            {submitProgress ? submitProgress : ""}
          </Text>
          <div className="add-tenor-table-popup__footer-actions">
            <CancelButton onClick={onClose} disabled={isSubmitting}>
              {MODAL_BUTTON_KEYS.CANCEL}
            </CancelButton>
            <PrimaryButton
              loading={isSubmitting}
              disabled={!rows.length}
              onClick={handleSubmit}
            >
              {MODAL_BUTTON_KEYS.SUBMIT}
            </PrimaryButton>
          </div>
        </div>
      }
    >
      {isUpdatingExistingTenor ? (
        <p className="add-tenor-table-popup__existing-banner">
          {EXISTING_TENOR_UPDATE_MESSAGE}
        </p>
      ) : null}
      <div className="add-tenor-table-popup__sheet-wrap">
        <AddTenorExcelGrid
          rows={rows}
          rowErrorsByKey={rowErrorsByKey}
          lockPcs={lockPcs}
          renderCell={renderCell}
          onRemoveRow={handleRemoveRow}
          removeRowDisabled={isSubmitting}
        />
      </div>
      <div className="add-tenor-table-popup__toolbar">
        <SecondaryButton
          icon={<PlusOutlined />}
          onClick={handleAddRow}
          disabled={isSubmitting}
        >
          {MODAL_BUTTON_KEYS.ADD_ROW}
        </SecondaryButton>
      </div>
    </Modal>
  );
}

export default AddTenorTablePopup;
